import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Hash, Plus, Trash2, ClipboardPaste } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Button, Input } from "@/components/ui";

// =============================================================================
// ClientRefsCard — mig 480. The client's numbers for this job (Mirage no.,
// R009 ref, NBN order numbers, site code). A docket carrying one of these
// matches the job by itself. Add one, or paste the column from the client's
// sheet. A number that is already on another job is reported, not moved.
// =============================================================================
interface Ref { ref_id: string; ref: string; kind: string; note: string | null; source: string; created_at: string }
const KIND_LABEL: Record<string, string> = { project_no: "project no.", order_no: "order no.", site_code: "site code", po: "PO", other: "other" };

export default function ClientRefsCard({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState("");
  const [kind, setKind] = useState("project_no");
  const [pasteOpen, setPasteOpen] = useState(false);
  const [paste, setPaste] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: refs = [] } = useQuery({
    queryKey: ["project_client_refs", projectId],
    queryFn: async () => { const { data } = await (supabase as any).from("project_client_refs").select("*").eq("project_id", projectId).order("created_at"); return (data ?? []) as Ref[]; },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["project_client_refs", projectId] });

  async function add(text: string, k: string) {
    setBusy(true); setMsg(null);
    const { data, error } = await (supabase as any).schema("app").rpc("project_client_refs_paste", { p_project_id: projectId, p_text: text, p_kind: k });
    setBusy(false);
    if (error) { setMsg(error.message); return; }
    const r = Array.isArray(data) ? data[0] : data;
    const parts = [r?.added ? `${r.added} added` : null, r?.already ? `${r.already} already here` : null, (r?.elsewhere ?? []).length ? `on another job: ${(r.elsewhere as string[]).join(", ")}` : null].filter(Boolean);
    setMsg(parts.join(" · ") || "Nothing to add");
    setAdding(""); setPaste(""); setPasteOpen(false); refresh();
  }
  async function remove(r: Ref) {
    if (!confirm(`Remove ${r.ref} from this job's numbers?`)) return;
    const { error } = await (supabase as any).from("project_client_refs").delete().eq("ref_id", r.ref_id);
    if (error) setMsg(error.message); else refresh();
  }

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <Hash size={14} className="text-accent" />
          <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold">The client's numbers for this job</span>
          <span className="text-[11px] text-text-dim">— a docket carrying one of these matches the job by itself</span>
          {canEdit && <button type="button" className="ml-auto text-[12px] text-accent hover:underline inline-flex items-center gap-1" onClick={() => setPasteOpen(v => !v)}><ClipboardPaste size={12} /> paste from the client's sheet</button>}
        </div>
        {refs.length === 0 && <div className="text-[12.5px] text-text-muted mb-2">None yet. Add the Mirage / R009 / order number from their paperwork, or paste the column from their sheet.</div>}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {refs.map(r => (
            <span key={r.ref_id} className="inline-flex items-center gap-1.5 border border-line rounded px-2 py-1 text-[12px] bg-ink-raised" title={`${KIND_LABEL[r.kind] ?? r.kind} · ${r.source}${r.note ? ` · ${r.note}` : ""}`}>
              <span className="font-mono">{r.ref}</span>
              <span className="text-[10px] text-text-dim">{KIND_LABEL[r.kind] ?? r.kind}</span>
              {canEdit && <button type="button" onClick={() => remove(r)} className="text-text-dim hover:text-danger" title="Remove"><Trash2 size={11} /></button>}
            </span>
          ))}
        </div>
        {canEdit && !pasteOpen && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-56"><Input mono value={adding} onChange={(e: any) => setAdding(e.target.value)} placeholder="e.g. 4022081.01 or 0004202752250100" onKeyDown={(e: any) => { if (e.key === "Enter" && adding.trim()) add(adding, kind); }} /></div>
            <select value={kind} onChange={e => setKind(e.target.value)} className="bg-ink-raised border border-line rounded px-2 py-1.5 text-[12px] text-text">
              {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <Button size="sm" variant="secondary" icon={Plus} disabled={busy || !adding.trim()} onClick={() => add(adding, kind)}>Add</Button>
          </div>
        )}
        {canEdit && pasteOpen && (
          <div className="space-y-2">
            <textarea rows={5} value={paste} onChange={e => setPaste(e.target.value)} placeholder={"One number per line (commas and tabs are fine too)\n0004202752250100\n0004202752250101"} className="w-full bg-ink-raised border border-line rounded-md px-3 py-2 text-[12.5px] font-mono outline-none focus:border-accent" />
            <div className="flex items-center gap-2">
              <select value={kind} onChange={e => setKind(e.target.value)} className="bg-ink-raised border border-line rounded px-2 py-1.5 text-[12px] text-text">
                {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <Button size="sm" disabled={busy || !paste.trim()} onClick={() => add(paste, kind)}>{busy ? "Adding…" : "Add them"}</Button>
              <Button size="sm" variant="ghost" onClick={() => { setPasteOpen(false); setPaste(""); }}>Cancel</Button>
            </div>
          </div>
        )}
        {msg && <div className="text-[12px] text-text-muted mt-2">{msg}</div>}
      </div>
    </Card>
  );
}
